/**
 * Membership Circles Section
 * 
 * Shows exclusive VIP circles based on user's membership tiers:
 * - Just Be Retreat VIP Circle (costa_rica tier)
 * - Inner Edge VIP Circle (inner_edge tier)
 * - Sister Diamonds Collective VIP Circle (sdc tier)
 * 
 * These link to actual VIP circles in the database, not community rooms.
 */

import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, ArrowRight, Sparkles, Crown, Mountain, Zap
} from 'lucide-react';

const API_URL = window.location.origin;

// Define the VIP circles configuration (matches backend VIP_CIRCLES)
const VIP_CIRCLE_CONFIG = {
  costa_rica: {
    id: 'vip-costa-rica',
    name: 'Just Be Retreat Circle',
    description: 'Private circle for Costa Rica Retreat attendees. Coordinate travel, share excitement, and stay connected before, during, and after our transformational retreat.',
    tierKey: 'has_costa_rica',
    icon: Mountain,
    gradient: 'from-emerald-600 to-teal-500'
  },
  inner_edge: {
    id: 'vip-inner-edge',
    name: 'Inner Edge VIP Circle',
    description: 'Exclusive private circle for Inner Edge members. Connect with your cohort, share breakthroughs, and support each other on your leadership journey.',
    tierKey: 'has_inner_edge',
    icon: Zap,
    gradient: 'from-purple-600 to-indigo-500'
  },
  sdc: {
    id: 'vip-sdc',
    name: 'Sister Diamonds Collective VIP Circle',
    description: 'The ultimate VIP experience. Exclusive access for SDC members to connect, collaborate, and elevate together at the highest level.',
    tierKey: 'has_sdc',
    icon: Crown,
    gradient: 'from-amber-500 to-orange-500'
  }
};

const MembershipCirclesSection = ({ className = '' }) => {
  const [userTiers, setUserTiers] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUserTiers();
  }, []);

  const fetchUserTiers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/user/tiers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserTiers(response.data);
    } catch (error) {
      console.error('Failed to fetch user tiers:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return null; // Don't show loading state - just hide until loaded
  }

  // Filter circles based on user's active tiers
  const availableCircles = Object.entries(VIP_CIRCLE_CONFIG)
    .filter(([tier, config]) => userTiers && userTiers[config.tierKey])
    .map(([tier, config]) => config);

  // Don't render if user doesn't have any special memberships
  if (availableCircles.length === 0) {
    return null;
  }

  return (
    <div className={`mb-8 ${className}`} data-testid="membership-circles-section">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="h-5 w-5 text-terracotta" />
        <h2 className="font-heading text-xl font-semibold text-forest">Your VIP Circles</h2>
      </div>
      
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {availableCircles.map((circle) => {
          const IconComponent = circle.icon;
          
          return (
            <Card 
              key={circle.id} 
              className="card-base overflow-hidden border-2 border-transparent hover:border-terracotta/30 transition-all"
              data-testid={`membership-circle-${circle.id}`}
            >
              <div className={`h-24 bg-gradient-to-r ${circle.gradient} relative`}>
                <div className="absolute inset-0 bg-black/10" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <IconComponent className="h-12 w-12 text-white/80" />
                </div>
                <Badge className="absolute top-2 right-2 bg-white/90 text-forest text-xs">
                  VIP Access
                </Badge>
              </div>
              
              <CardContent className="p-4">
                <h3 className="font-heading font-semibold text-forest mb-2">
                  {circle.name}
                </h3>
                <p className="text-sm text-gray-600 line-clamp-2 mb-4">
                  {circle.description}
                </p>
                
                <Link to={`/circles/${circle.id}`}>
                  <Button 
                    className="w-full btn-primary"
                    data-testid={`enter-${circle.id}`}
                  >
                    <Users className="h-4 w-4 mr-2" />
                    Enter Circle
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default MembershipCirclesSection;
